import { syncAllCreditCardVuetifyOverrides } from '@/utils/creditCardVuetifyOverridesSync';
import {
  syncAvwcContrastTokens,
  syncPaymentMethodDialogPortalTokens,
} from '@/utils/colorContrast';

const ATTACH_SELECTOR =
  '.authvia-pm-dialog-attach, [data-testid="payment-method-dialog-attach"]';
const ACTIVE_OVERLAY_SELECTOR = '.authvia-pm-dialog-attach .v-overlay--active';
export const PAYMENT_METHOD_DIALOG_PORTAL_ID = 'authvia-pm-dialog-portal';

/**
 * Payment-method-action mounts dialog overlays on a node inside
 * .payment-method-dialog-host, which sits under .pm-exp-border (overflow: hidden)
 * and below the fixed merchant header. Move that node to a body portal so the
 * modal stacks above the header without restyling the payment-method list.
 */
export function getPaymentMethodDialogPortal(): HTMLElement {
  let portal = document.getElementById(PAYMENT_METHOD_DIALOG_PORTAL_ID);
  if (!portal) {
    portal = document.createElement('div');
    portal.id = PAYMENT_METHOD_DIALOG_PORTAL_ID;
    portal.className = 'authvia-pm-dialog-portal';
    portal.setAttribute('data-testid', PAYMENT_METHOD_DIALOG_PORTAL_ID);
    document.body.appendChild(portal);
  }
  return portal;
}

export function hasActivePaymentMethodOverlay(): boolean {
  return document.querySelector(ACTIVE_OVERLAY_SELECTOR) !== null;
}

export function reparentPaymentMethodDialogAttach(node: HTMLElement): void {
  const portal = getPaymentMethodDialogPortal();
  if (node.parentElement === portal) {
    return;
  }
  portal.appendChild(node);
}

export function syncPaymentMethodDialogAttachTargets(): void {
  syncPaymentMethodDialogPortalTokens(getPaymentMethodDialogPortal());

  document.querySelectorAll(ATTACH_SELECTOR).forEach((node: Element) => {
    const attach: HTMLElement = node as HTMLElement;
    reparentPaymentMethodDialogAttach(attach);
    syncAvwcContrastTokens(attach);
  });

  syncAllCreditCardVuetifyOverrides();
}

let observer: MutationObserver | undefined;
let rafId: number | undefined;

function stopReparentLoop(): void {
  if (rafId !== undefined) {
    cancelAnimationFrame(rafId);
    rafId = undefined;
  }
}

/**
 * Vue owns the attach node, so a re-render can pull it back out of the portal.
 * Keep re-syncing for as long as a dialog is open.
 */
function reparentLoop(): void {
  syncPaymentMethodDialogAttachTargets();
  if (hasActivePaymentMethodOverlay()) {
    rafId = requestAnimationFrame(reparentLoop);
  } else {
    stopReparentLoop();
  }
}

function scheduleReparent(): void {
  syncPaymentMethodDialogAttachTargets();
  if (!hasActivePaymentMethodOverlay()) {
    stopReparentLoop();
    return;
  }
  if (rafId === undefined) {
    rafId = requestAnimationFrame(reparentLoop);
  }
}

export function startPaymentMethodDialogAttachObserver(): () => void {
  getPaymentMethodDialogPortal();
  syncPaymentMethodDialogAttachTargets();

  observer = new MutationObserver(scheduleReparent);
  observer.observe(document.body, { childList: true, subtree: true });

  return () => {
    observer?.disconnect();
    observer = undefined;
    stopReparentLoop();
  };
}
